import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Input, Badge, Spinner } from 'reactstrap';
import { API_WEB_URLS } from 'constants/constAPI';

const ContractSearchDropdown = ({
    value,
    onChange,
    onSelect,
    contracts = [],
    voucherDate = '',
    loading = false,
    placeholder = "Search or select Contract No...",
    name = "contractNo",
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [remoteContracts, setRemoteContracts] = useState(null);
    const [searching, setSearching] = useState(false);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const activeSearchQueryRef = useRef('');

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Perform remote search across all contracts
    const performRemoteSearch = useCallback(async (query) => {
        if (!query || typeof query !== 'string' || query.trim() === '') {
            setRemoteContracts(null);
            setSearching(false);
            return;
        }

        const trimmed = query.trim();
        activeSearchQueryRef.current = trimmed;
        setSearching(true);

        try {
            const authToken = JSON.parse(localStorage.getItem("authUser"))?.token;
            const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

            // Format: VoucherDate|SearchQuery
            const param = `${voucherDate || ''}|${trimmed}`;
            const url = `${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token/Top50Contracts/${encodeURIComponent(param)}/0`;

            const response = await fetch(url, { headers });
            if (response.ok) {
                const resJson = await response.json();
                const list = resJson?.data?.dataList || resJson?.data || [];
                // Check if this response is for the latest query
                if (activeSearchQueryRef.current === trimmed) {
                    setRemoteContracts(Array.isArray(list) ? list : []);
                }
            }
        } catch (err) {
            console.error("Error searching contracts:", err);
        } finally {
            if (activeSearchQueryRef.current === trimmed) {
                setSearching(false);
            }
        }
    }, [voucherDate]);

    // When value changes, trigger debounced remote search
    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (!value || typeof value !== 'string' || value.trim() === '') {
            setRemoteContracts(null);
            setSearching(false);
            activeSearchQueryRef.current = '';
            return;
        }

        const query = value.trim();
        debounceTimerRef.current = setTimeout(() => {
            performRemoteSearch(query);
        }, 250);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [value, performRemoteSearch]);

    // Active displayed contracts:
    // If user has typed a query and remoteContracts is loaded, show remoteContracts.
    // Otherwise fallback to contracts (which are date-matched top 50), filtered by current input while remote loads.
    const displayContracts = useMemo(() => {
        if (remoteContracts !== null) {
            return remoteContracts;
        }
        const list = contracts || [];
        if (!value || typeof value !== 'string' || value.trim() === '') {
            return list;
        }
        const query = value.trim().toLowerCase();
        return list.filter(c =>
            (c.ContractNo && String(c.ContractNo).toLowerCase().includes(query)) ||
            (c.Buyer && String(c.Buyer).toLowerCase().includes(query)) ||
            (c.Seller && String(c.Seller).toLowerCase().includes(query)) ||
            (c.Item && String(c.Item).toLowerCase().includes(query))
        );
    }, [remoteContracts, contracts, value]);

    const handleItemClick = (contract) => {
        if (onSelect) {
            onSelect(contract);
        }
        setRemoteContracts(null);
        setIsOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setRemoteContracts(null);
        setSearching(false);
        activeSearchQueryRef.current = '';
        if (onChange) {
            onChange({ target: { name, value: '' } });
        }
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
        } else if (e.key === 'ArrowDown') {
            if (!isOpen) setIsOpen(true);
        } else if (e.key === 'Enter') {
            if (isOpen && displayContracts.length === 1) {
                e.preventDefault();
                handleItemClick(displayContracts[0]);
            }
        }
    };

    return (
        <div ref={wrapperRef} className="position-relative w-100" style={{ zIndex: isOpen ? 1050 : 1 }}>
            <div className="input-group input-group-sm">
                <Input
                    innerRef={inputRef}
                    type="text"
                    name={name}
                    value={value || ''}
                    placeholder={placeholder}
                    autoComplete="off"
                    disabled={disabled}
                    bsSize="sm"
                    onFocus={() => setIsOpen(true)}
                    onClick={() => setIsOpen(true)}
                    onChange={onChange}
                    onKeyDown={handleKeyDown}
                    style={{
                        paddingRight: value ? '48px' : '28px',
                        fontWeight: '500'
                    }}
                />

                {/* Right Action Icons (Clear + Toggle) */}
                <div
                    className="position-absolute end-0 top-50 translate-middle-y d-flex align-items-center pe-2 gap-1"
                    style={{ zIndex: 5 }}
                >
                    {(loading || searching) && (
                        <Spinner size="sm" color="primary" style={{ width: '12px', height: '12px', borderWidth: '1.5px' }} />
                    )}
                    {value && !disabled && (
                        <span
                            onClick={handleClear}
                            style={{ cursor: 'pointer', color: '#6c757d', fontSize: '14px', lineHeight: 1 }}
                            title="Clear"
                        >
                            &times;
                        </span>
                    )}
                    <span
                        onClick={() => !disabled && setIsOpen(!isOpen)}
                        style={{ cursor: 'pointer', color: '#6c757d', fontSize: '11px', lineHeight: 1 }}
                        title={isOpen ? "Close list" : "Show Top 50 Contracts"}
                    >
                        <i className={`bx ${isOpen ? 'bx-chevron-up' : 'bx-chevron-down'}`}></i>
                    </span>
                </div>
            </div>

            {/* Dropdown Menu */}
            {isOpen && !disabled && (
                <div
                    className="position-absolute bg-white rounded shadow-lg border"
                    style={{
                        top: 'calc(100% + 4px)',
                        left: 0,
                        minWidth: '320px',
                        maxWidth: '420px',
                        width: '100%',
                        zIndex: 1060,
                        overflow: 'hidden'
                    }}
                >
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center px-2 py-1 bg-light border-bottom font-size-11">
                        <span className="text-muted fw-bold">
                            {remoteContracts !== null ? (
                                <>
                                    <i className="bx bx-search-alt me-1 text-primary"></i>
                                    Matching Contracts ({displayContracts.length})
                                </>
                            ) : (
                                <>
                                    <i className="bx bx-list-ul me-1 text-primary"></i>
                                    Top 50 Contracts {voucherDate ? `(${voucherDate})` : ''}
                                </>
                            )}
                        </span>
                        <span className="text-muted font-size-10">Click to fill</span>
                    </div>

                    {/* Scrollable list */}
                    <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                        {searching && (!displayContracts || displayContracts.length === 0) ? (
                            <div className="text-center p-3 text-muted font-size-11">
                                <Spinner size="sm" color="primary" className="me-2" /> Searching all contracts...
                            </div>
                        ) : loading && (!contracts || contracts.length === 0) ? (
                            <div className="text-center p-3 text-muted font-size-11">
                                <Spinner size="sm" color="primary" className="me-2" /> Loading contracts...
                            </div>
                        ) : displayContracts.length === 0 ? (
                            <div className="p-3 text-center text-muted font-size-11">
                                <i className="bx bx-search-alt font-size-18 d-block mb-1 text-secondary"></i>
                                Koi contract nahi mila.
                            </div>
                        ) : (
                            displayContracts.map((c, idx) => (
                                <div
                                    key={c.Id || idx}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleItemClick(c);
                                    }}
                                    className="p-2 border-bottom"
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: String(value) === String(c.ContractNo) ? '#e8f0fe' : 'transparent',
                                        transition: 'background-color 0.15s'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (String(value) !== String(c.ContractNo)) {
                                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (String(value) !== String(c.ContractNo)) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                >
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <Badge color="soft-primary" className="font-size-11 text-dark fw-bold">
                                            #{c.ContractNo}
                                        </Badge>
                                        <span className="text-muted font-size-10">{c.ContractDate || ''}</span>
                                        <span className="fw-bold text-success font-size-11">
                                            ₹{parseFloat(c.Rate || 0).toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="font-size-11 text-truncate" title={`${c.Buyer} → ${c.Seller}`}>
                                        <span className="text-primary fw-medium">{c.Buyer || 'Buyer'}</span>
                                        <span className="mx-1 text-muted">&rarr;</span>
                                        <span className="text-danger fw-medium">{c.Seller || 'Seller'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between font-size-10 text-muted mt-1">
                                        <span>Item: <strong className="text-dark">{c.Item || '-'}</strong></span>
                                        <span>Qty: <strong className="text-dark">{c.Qty || 0} Ton</strong></span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContractSearchDropdown;
